// src/components/messaging/ThreadList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import useMatrixClient from '../../hooks/useMatrixClient';
import { useMessagingStore } from '../../state/messagingStore';
import { useMatrixStore } from '../../state/matrixStore';

// --- helpers ---------------------------------------------------------------

// real user (e.g. @u109:domain), never admin/bot
const isRealUserMxid = (id) => /^@u\d+:/i.test(String(id || ''));
const isAdminMxid    = (id) => /^@?admin(?::|$)/i.test(String(id || ''));

// initials for fallback avatar
function initialsOf(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function tinyWhen(ts) {
  const t = Number(ts || 0);
  if (!t) return '';
  const d = new Date(t);
  const now = new Date();

  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === y.getFullYear() &&
    d.getMonth() === y.getMonth() &&
    d.getDate() === y.getDate();
  if (isYesterday) return 'Yesterday';

  const opts = { month: 'numeric', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) Object.assign(opts, { year: '2-digit' });
  return d.toLocaleDateString([], opts);
}
// Return the best timestamp for an invite:
// 1) my own invite event (if I’m the invitee)
// 2) the peer’s invite event (if I’m the inviter)
// 3) room create event (last-resort)
function inviteTimestamp(room, myMxid) {
  try {
    // 1) If I am invited, use my member invite state event timestamp
    const me = room.getMember?.(myMxid);
    const meInviteTs =
      me?.membership === 'invite'
        ? (me?.events?.member?.getTs?.() ??
           me?.events?.member?.event?.origin_server_ts ??
           0)
        : 0;
    if (meInviteTs) return meInviteTs;

    // 2) Otherwise look for the peer’s invite event (I’m the inviter)
    const others = room.getMembers?.() || [];
    const peer = others.find(m => m?.userId && m.userId !== myMxid && m.membership === 'invite');
    const peerInviteTs =
      peer?.events?.member?.getTs?.() ??
      peer?.events?.member?.event?.origin_server_ts ??
      0;
    if (peerInviteTs) return peerInviteTs;

    // 3) Fallback: m.room.create event
    const ev = room?.currentState?.getStateEvents?.('m.room.create', '');
    const createTs = ev?.getTs?.() ?? ev?.event?.origin_server_ts ?? 0;
    return createTs || 0;
  } catch {
    return 0;
  }
}
// Canonical 1:1 DM alias: #eh.dm.u<min>_u<max>:domain
const DM_ALIAS_RE = /^#eh\.dm\.u(\d+)_u(\d+):/i;

function uidFromMxid(mxid) {
  const m = String(mxid || '').match(/^@u(\d+):/i);
  return m ? Number(m[1]) : null;
}

// Try the canonical alias first; if missing, fall back to room members
function peerUidFromRoom(room, myMxid) {
  // 1) Alias path
  const alias = room.getCanonicalAlias?.() || (room.getAliases?.() || [])[0] || '';
  const am = String(alias).match(DM_ALIAS_RE);
  if (am) {
    const a = Number(am[1]), b = Number(am[2]);
    const me = uidFromMxid(myMxid);
    if (Number.isFinite(me)) {
      return (me === a) ? b : (me === b ? a : null);
    }
    return a; // fallback: pick first
  }

  // 2) Members path
  try {
    const list = room.getMembers?.() || [];
    const other = list.find(m => m?.userId && m.userId !== myMxid && isRealUserMxid(m.userId));
    return other ? uidFromMxid(other.userId) : null;
  } catch { return null; }
}

// Convert peer's mxc avatar → http(s) img
function peerAvatarHttp(room, mxClient, myUserId) {
  try {
    const list = room.getMembers?.() || [];
    const peer =
      list.find(m => m?.userId && m.userId !== myUserId && isRealUserMxid(m.userId)) || null;
    if (!peer) return null;
    const mxc = peer.getMxcAvatarUrl?.();
    if (mxc && mxClient?.mxcUrlToHttp) return mxClient.mxcUrlToHttp(mxc, 28, 28, 'crop');
  } catch {}
  return null;
}

// other participant name (prefer real user, ignore admin)
function roomDisplay(room, myUserId) {
  try {
    const joined = room.getJoinedMembers?.() || [];
    const peer =
      joined.find(m => m?.userId !== myUserId && isRealUserMxid(m.userId)) ||
      (room.getMembers?.() || []).find(m => m?.userId !== myUserId && isRealUserMxid(m.userId)) ||
      null;
    if (peer?.name) return peer.name;
    if (peer?.userId) return peer.userId;
  } catch {}
  return room.getCanonicalAlias?.() || room.roomId;
}

// strict 1:1 — exactly two real users, no admin
function isStrictDM(room) {
  try {
    const all = room.getMembers?.() || [];
    const ids = all
      .filter(m => /^(join|invite)$/i.test(String(m?.membership || '')))
      .map(m => m?.userId)
      .filter(id => id && isRealUserMxid(id) && !isAdminMxid(id));
    const uniq = Array.from(new Set(ids));
    return uniq.length === 2;
  } catch { return false; }
}

// last text preview + ts from timeline (optional)
function lastText(room) {
  try {
    const events =
      (room.getLiveTimeline?.().getEvents?.() || []) ||
      room.timeline || [];
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if (ev?.getType?.() !== 'm.room.message') continue;
      const c = ev.getContent?.() || {};
      if (c.msgtype !== 'm.text') continue;
      const body = (c.body || '').toString().trim();
      if (!body) continue;
      return { text: body, ts: Number(ev.getTs?.() || 0) || 0 };
    }
  } catch {}
  return { text: null, ts: 0 };
}

// --- component -------------------------------------------------------------

export default function ThreadList() {
  const { client: mxClient, isReady: mxReady } = useMatrixClient();
  const selectedThread = useMessagingStore(s => s.selectedThread);
  const setSelectedThread = useMessagingStore(s => s.setSelectedThread);
  const peerProfiles = useMatrixStore(s => s.userProfiles);
  const setPeerProfile = useMatrixStore(s => s.setUserProfile);
  const optimisticThreads = useMessagingStore(s => s.optimisticThreads);
  const reconcileDM = useMessagingStore(s => s.reconcileDM);

    // NEW: when Matrix reports a new room (or we join one), reconcile the optimistic row by alias
    useEffect(() => {
    if (!mxReady || !mxClient) return;

    const onRoom = (room) => {
        try {
        const alias = room.getCanonicalAlias?.() || (room.getAliases?.()?.[0] || null);
        if (alias) {
            // This removes the optimistic entry (if any) and lets the real room render in-place
            reconcileDM({ alias, roomId: room.roomId });
        }
        } catch (e) {
        // no-op
        }
    };

    mxClient.on('Room', onRoom);
    return () => {
        mxClient.removeListener('Room', onRoom);
    };
    }, [mxReady, mxClient, reconcileDM]);

  // prevent duplicate fetches per uid
  const [fetching, setFetching] = useState(() => new Set());

  const [version, setVersion] = useState(0);
  const [hasInitialData, setHasInitialData] = useState(false);

  /* bump on room/timeline/state/sync so list stays live
  useEffect(() => {
    if (!mxClient) return;
    const bump = () => setVersion(v => v + 1);
    mxClient.on('Room', bump);
    mxClient.on('Room.timeline', bump);
    mxClient.on('RoomState.events', bump);
    mxClient.on('sync', bump);
    return () => {
      mxClient.removeListener('Room', bump);
      mxClient.removeListener('Room.timeline', bump);
      mxClient.removeListener('RoomState.events', bump);
      mxClient.removeListener('sync', bump);
    };
  }, [mxClient]);
  */

    // Replace the current items useMemo with this optimized version:
    const items = useMemo(() => {
    // NEW: Return empty if no initial data
    if (!hasInitialData) {
      return [];
    }
    // Use preloaded data from matrixStore instead of direct Matrix client
    const matrixRooms = useMatrixStore.getState().getSortedRooms();
    const userProfiles = useMatrixStore.getState().userProfiles; // Get all preloaded profiles
    
    if (!matrixRooms.length) return [];
    
    const myMxid = mxClient?.getUserId?.() || '';

    // Map store rooms to thread list items
    const mapped = matrixRooms.map((room) => {
        let peerUid = room.peerUserId;
        if (!peerUid && room.alias) {
        const m = String(room.alias).match(/^#eh\.dm\.u(\d+)_u(\d+):/i);
        if (m) {
            const a = Number(m[1]), b = Number(m[2]);
            const me = uidFromMxid(myMxid);
            peerUid = Number.isFinite(me) ? (me === a ? b : a) : a;
        }
        }

        const alias = room.alias;
        const joinable = alias || room.roomId;
        
        // Use preloaded user profiles from matrixStore (instant access)
        const userProfile = userProfiles[peerUid];
        const peerName = userProfile?.name || null;
        const title = peerName || room.name || 'Unknown';
        const avatarUrl = userProfile?.avatarUrl || room.avatarUrl || null;
        
        // FIX: Proper invite status detection
        const isInvite = room.membership === 'invite';
        const preview = isInvite ? 'Chat Request Pending' : (room.lastMessage || 'No messages yet');
        
        const previewWhen = tinyWhen(room.lastMessageTs);
        const lastActive = room.lastMessageTs || room.lastUpdated || 0;

        return {
        id: room.roomId,
        joinable,
        alias,
        membership: room.membership || 'join',
        title,
        avatarUrl,
        preview,
        previewWhen,
        lastActive,
        peerUid,
        };
    });

    // Sort: invites first (newest), then joined (newest)
    const byNewest = (a, b) => (b.lastActive || 0) - (a.lastActive || 0);
    const invites = mapped.filter(m => m.membership === 'invite').sort(byNewest);
    const joined = mapped.filter(m => m.membership === 'join').sort(byNewest);

    return [...invites, ...joined];
    }, [version, hasInitialData]); // Add version dependency to re-render when store updates

  // Optimistic rows come from the store (created by openWithCandidate).
  // They render instantly with name/avatar/preview/time before Matrix sync.
  const optimisticItems = useMemo(() => {
    if (!optimisticThreads || !optimisticThreads.length) return [];
    return optimisticThreads
      .map(t => ({
        id: `opt:${t.alias}`,
        joinable: t.alias,                 // we select by alias
        alias: t.alias,
        membership: 'optimistic',
        title: t.displayName || (t.peerUid ? `User ${t.peerUid}` : 'User'),
        avatarUrl: t.avatarUrl || null,
        preview: t.lastMessagePreview || 'Start a conversation…',
        previewWhen: tinyWhen(t.lastMessageAt || Date.now()),
        lastActive: Number(t.lastMessageAt || Date.now()),
        peerUid: t.peerUid,
        isOptimistic: true,
      }))
      // newest optimistic at top
      .sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
  }, [optimisticThreads]);

    // Listen for matrixStore room updates (standard subscribe — no middleware needed)
    useEffect(() => {
    let prevRooms = useMatrixStore.getState().rooms;

    const unsubscribe = useMatrixStore.subscribe((state) => {
        if (state.rooms !== prevRooms) {
          prevRooms = state.rooms;
          setVersion(v => v + 1);

          const hasRooms = Object.keys(state.rooms || {}).length > 0;
          if (hasRooms && !hasInitialData) {
            setHasInitialData(true);
          }
        }
    });

    // Check immediately on mount
    const currentRooms = useMatrixStore.getState().rooms;
    const hasRoomsNow = Object.keys(currentRooms || {}).length > 0;

    if (hasRoomsNow && !hasInitialData) {
      setHasInitialData(true);
    }

    return unsubscribe;
    }, [hasInitialData]);


  const listHasSelected = useMemo(() => {
    if (!selectedThread) return false;
    const hitReal = items.some(it =>
      String(it.joinable) === String(selectedThread) ||
      String(it.id) === String(selectedThread)
    );
    if (hitReal) return true;
    const hitOpt = optimisticItems.some(it =>
      String(it.joinable) === String(selectedThread) ||
      String(it.id) === String(selectedThread)
    );
    return hitOpt;
  }, [items, optimisticItems, selectedThread]);


  const showSkeleton = !!selectedThread &&
    /^#eh\.dm\.u\d+_u\d+:/i.test(String(selectedThread)) &&
    !listHasSelected;

  // Derive the peer uid for the skeleton from the selectedThread alias
  let skeletonPeerUid = null;
  if (showSkeleton) {
    const m = String(selectedThread).match(/^#eh\.dm\.u(\d+)_u(\d+):/i);
    if (m) {
      const a = Number(m[1]), b = Number(m[2]);
      const me = uidFromMxid(mxClient?.getUserId?.() || '');
      skeletonPeerUid = Number.isFinite(me) ? (me === a ? b : a) : a;
    }
  }
  const skeletonProfile = skeletonPeerUid ? peerProfiles?.[skeletonPeerUid] : null;

  const onSelect = (it) => {
    // never auto-join here; ThreadView handles Accept/Decline
    setSelectedThread(it.joinable);
  };

  // Prefer the real Matrix room if it exists; otherwise show the optimistic row.
  // Track real aliases we’ve seen (when available)…
  const realAliases = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      if (it?.alias) set.add(String(it.alias));
    }
    return set;
  }, [items]);

  // …and ALSO track real peer UIDs (fallback when alias isn’t populated yet).
  const realPeerUids = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      if (Number.isFinite(it?.peerUid)) set.add(Number(it.peerUid));
    }
    return set;
  }, [items]);

  const mergedItems = useMemo(() => {
    // keep only optimistic rows that don't yet have a real room
    // primary: drop if a real row with SAME alias exists
    // fallback: drop if a real row with SAME peerUid exists (alias not ready yet)
    const onlyOptimistic = optimisticItems.filter(o => {
      const aliased = o.alias && realAliases.has(String(o.alias));
      const peerHit = Number.isFinite(o.peerUid) && realPeerUids.has(Number(o.peerUid));
      return !(aliased || peerHit);
    });
    return [...onlyOptimistic, ...items];
  }, [optimisticItems, items, realAliases, realPeerUids]);

  // NEW: Show loading state until data is ready
  if (!hasInitialData) {
    return (
      <div className="h-full overflow-y-auto relative scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-100">
        <div className="p-3 sm:p-4">
          <div className="flex justify-center items-center py-8">
            <div className="text-sm text-gray-500">Loading conversations...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto relative scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-100">
      <div className="p-3 sm:p-4">
        {(!mergedItems.length && !showSkeleton) ? (
          <ul className="space-y-1">
            <li className="p-2 rounded-md border border-dashed border-gray-200 text-gray-400 text-sm">
              (no conversations yet)
            </li>
          </ul>
        ) : (
          <ul className="space-y-1">
            {showSkeleton ? (
              <li>
                <div className="w-full flex items-center gap-3 rounded-md px-2 py-2 border border-dashed border-[#00aef7]/50 bg-[#E9F8FF] animate-pulse">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 overflow-hidden">
                    {skeletonProfile?.avatarUrl ? (
                      <img src={skeletonProfile.avatarUrl} alt="" className="h-7 w-7 object-cover" />
                    ) : null}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-xs font-medium text-gray-900">
                        {skeletonProfile?.name || 'Starting chat…'}
                      </div>
                      <span className="ml-auto shrink-0 text-[10px] leading-4 text-gray-400">…</span>
                    </div>
                    <div className="text-[11px] leading-4 text-gray-500 truncate">Creating room…</div>
                  </div>
                </div>
              </li>
            ) : null}

            {mergedItems.map((it) => {
              const isActive = !!selectedThread &&
                (String(selectedThread) === String(it.joinable) || String(selectedThread) === String(it.id));

              return (
                <li key={it.id}>
                  <button
                    onClick={() => onSelect(it)}
                    className={
                      'w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition ' +
                      (isActive ? 'bg-[#E9F8FF] border border-[#00aef7]' : 'hover:bg-gray-50 border border-transparent')
                    }
                    title={it.title}
                  >
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-700 text-xs font-semibold overflow-hidden">
                      {it.avatarUrl ? (
                        <img src={it.avatarUrl} alt="" className="h-7 w-7 object-cover" />
                      ) : (
                        initialsOf(it.title)
                      )}
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-xs font-medium text-gray-900">{it.title}</div>
                        {it.previewWhen ? (
                          <span className="ml-auto shrink-0 text-[10px] leading-4 text-gray-400">{it.previewWhen}</span>
                        ) : null}
                      </div>
                      <div className="text-[11px] leading-4 text-gray-500 truncate">
                        {it.preview}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
